@if (isExtracting()) {
  <div class="reextraction-message">
    <div class="reextraction-content">
      <div class="reextraction-header">
        <i class="fa-solid fa-spinner fa-spin reextraction-icon"></i>
        <h2>{{ 'ITP.extractingTranscriptHeader' | translate }}</h2>
      </div>

      <div class="fun-facts">
        <p class="facts-header">{{ 'randomFactsHeader' | translate }}</p>
        @if (randomFact()) {
          <p class="fact-text">{{ randomFact() }}</p>
        }
      </div>
    </div>
    <div class="reextraction-close">
      <p-button
        class="dap-close-reextraction"
        type="button"
        variant="outlined"
        severity="secondary"
        [label]="'close' | translate"
        [attr.aria-label]="'close' | translate"
        (click)="onClose()"
      ></p-button>
    </div>
  </div>
} @else if (dataLoadError()) {
  <div class="error-message">
    <i class="fa-solid fa-circle-exclamation error-icon"></i>
    @if (dataLoadErrorReason() === 'typeChange') {
      <h2>{{ 'transcriptData.accessRevokedHeader' | translate }}</h2>
      <p>{{ 'transcriptData.accessRevokedMessage' | translate }}</p>
    } @else {
      <h2>{{ 'transcriptData.noTranscriptDataHeader' | translate }}</h2>
      <p>{{ 'transcriptData.noTranscriptDataMessage' | translate }}</p>
    }
    <p-button
      class="dap-close-error"
      type="button"
      variant="text"
      severity="secondary"
      [label]="'close' | translate"
      [attr.aria-label]="'close' | translate"
      (click)="onClose()"
    ></p-button>
  </div>
} @else {
  <div class="extraction-data-header">
    <div class="head-label">
      <div class="data-header">
        @if (currentDocument()?.typeMismatch && itpTypeFilterFlag()) {
          <i
            class="fa-duotone fa-solid fa-triangle-exclamation warning-icon"
            role="img"
            tooltipPosition="top"
            showDelay="500"
            [attr.aria-label]="'ITP.typeMismatchTooltip' | translate"
            [pTooltip]="'ITP.typeMismatchTooltip' | translate"
          ></i>
        }
        <span>{{ getTypeLabel(transcriptType()) }} {{ 'dataExtraction' | translate }}</span>
      </div>
      @if (itpTypeFilterFlag() && isItpPowerProcessor()) {
        <div class="xs-btn-container">
          <p-button
            #typeButton
            class="change-type-btn dap-change-type"
            variant="outlined"
            severity="secondary"
            [label]="'ITP.changeType' | translate"
            [disabled]="!isEditable()"
            (click)="typeMenu.toggle($event)"
          ></p-button>
          <p-menu
            #typeMenu
            appendTo="body"
            styleClass="type-menu-panel"
            [popup]="true"
            [model]="transcriptTypeMenuItems()"
            (onHide)="typeButton.el.nativeElement.querySelector('button')?.focus()"
          ></p-menu>
        </div>
      }
      @if (isEditable() && (hasBeenEdited() || isSaving() || saveFailed())) {
        <span
          class="save-status"
          [ngClass]="{
            saving: isSaving(),
            'up-to-date': !isSaving() && !hasUnsavedChanges() && !saveFailed(),
            'save-failed': saveFailed()
          }"
        >
          <i
            class="fa-solid"
            [ngClass]="{
              'fa-spinner-scale fa-spin-pulse': isSaving() || hasUnsavedChanges(),
              'fa-circle-check': !isSaving() && !saveFailed(),
              'fa-circle-exclamation': saveFailed()
            }"
          ></i>
          {{
            isSaving()
              ? ('savingChanges' | translate)
              : saveFailed()
                ? 'Failed to Save'
                : hasUnsavedChanges()
                  ? ('changesDetected' | translate)
                  : ('allChangesSaved' | translate)
          }}
        </span>
      }
    </div>

    <div class="header-actions">
      @if (isLocked()) {
        <i
          class="fa-solid locked-by-user"
          role="img"
          tabindex="0"
          tooltipPosition="top"
          showDelay="500"
          tooltipEvent="both"
          [ngClass]="isLockedByCurrentUser() ? 'fa-lock' : 'fa-user-lock'"
          [attr.aria-label]="('ITP.assignedTo' | translate) + ' ' + lockedByUser()"
          [pTooltip]="('ITP.assignedTo' | translate) + ' ' + lockedByUser()"
        ></i>

        @if (isLockedByCurrentUser() || hasItpAdminRole()) {
          <p-button
            class="dap-unassign-transcript"
            severity="secondary"
            variant="outlined"
            size="small"
            [label]="'transcriptData.unassign' | translate"
            [disabled]="isExporting()"
            (click)="unlockTranscript()"
          ></p-button>
        }
      } @else if (transcriptStatusId() !== TranscriptStatus.Archived) {
        <p-button
          class="dap-edit-transcript"
          severity="secondary"
          size="small"
          icon="fa-solid fa-pen-to-square"
          [label]="'ITP.edit' | translate"
          [disabled]="isExporting()"
          (click)="lockTranscriptForEditing()"
        ></p-button>
      } @else if (transcriptStatusId() === TranscriptStatus.Archived) {
        <p-button
          class="dap-assign-transcript"
          severity="secondary"
          size="small"
          [label]="'ITP.assignTo' | translate"
          [disabled]="isExporting()"
          (click)="lockTranscriptForEditing()"
        ></p-button>
      }
      <p-button
        pTooltip="{{ 'downloadTranscriptData' | translate }}"
        class="dap-download-transcript"
        icon="fa-solid fa-arrow-down-to-bracket"
        tooltipPosition="top"
        showDelay="1000"
        severity="secondary"
        [ariaLabel]="'downloadTranscriptData' | translate"
        [disabled]="!transcriptData || isExporting()"
        [text]="true"
        (click)="downloadTranscriptData()"
      ></p-button>
    </div>
  </div>

  <div class="data-panels">
    @if (transcriptData) {
      <div class="info-panels-container">
        <!-- Student Information Panel -->
        <p-panel [toggleable]="true">
          <ng-template
            let-collapsed
            pTemplate="headericons"
          >
            <i [class]="collapsed ? 'fa-solid fa-caret-right' : 'fa-solid fa-caret-down'"></i>
          </ng-template>
          <ng-template
            let-collapsed
            pTemplate="header"
          >
            <div class="custom-panel-header">{{ 'transcriptData.student' | translate }}</div>
          </ng-template>
          <div class="panel-content">
            <div class="field-rows">
              @for (row of studentFieldRows; track $index) {
                <div class="field-row">
                  @for (field of row; track field.id) {
                    <div class="field-item">
                      <label [for]="field.id">
                        {{ 'transcriptData.' + field.fieldKey | translate }}
                        @if (showRequiredElement(field.fieldKey, 1, '')) {
                          <span class="error-label">*</span>
                        }
                      </label>
                      <div class="input-wrapper">
                        @if (
                          shouldShowFieldError(
                            field.fieldKey,
                            transcriptData.Student[field.modelKey].value
                          )
                        ) {
                          <i class="fa-solid fa-circle-exclamation error-icon"></i>
                        } @else if (
                          fieldsNeedingReview(
                            field.fieldKey,
                            transcriptData.Student[field.modelKey]
                          )
                        ) {
                          <i class="fa-regular fa-triangle-exclamation warning-icon"></i>
                        }
                        <input
                          pInputText
                          [id]="field.id"
                          [readonly]="!isEditable()"
                          [required]="showRequiredElement(field.fieldKey, 1, '')"
                          [attr.aria-required]="showRequiredElement(field.fieldKey, 1, '')"
                          [attr.aria-invalid]="
                            shouldShowFieldError(
                              field.fieldKey,
                              transcriptData.Student[field.modelKey].value
                            )
                          "
                          [attr.aria-describedby]="
                            shouldShowFieldError(
                              field.fieldKey,
                              transcriptData.Student[field.modelKey].value
                            )
                              ? field.id + '-error'
                              : null
                          "
                          [class.has-warning]="
                            fieldsNeedingReview(
                              field.fieldKey,
                              transcriptData.Student[field.modelKey]
                            ) &&
                            !shouldShowFieldError(
                              field.fieldKey,
                              transcriptData.Student[field.modelKey].value
                            )
                          "
                          [class.has-error]="
                            shouldShowFieldError(
                              field.fieldKey,
                              transcriptData.Student[field.modelKey].value
                            )
                          "
                          [(ngModel)]="transcriptData.Student[field.modelKey].value"
                          (ngModelChange)="onFieldChange()"
                          (click)="showBoundingBox(transcriptData.Student[field.modelKey])"
                          (focus)="
                            onInputFocus(
                              transcriptData.Student[field.modelKey].value,
                              field.fieldKey,
                              transcriptData.Student[field.modelKey]
                            )
                          "
                        />
                        @if (
                          shouldShowFieldError(
                            field.fieldKey,
                            transcriptData.Student[field.modelKey].value
                          )
                        ) {
                          <span
                            class="sr-only"
                            role="alert"
                            [id]="field.id + '-error'"
                          >
                            {{ 'transcriptData.errors.' + field.fieldKey + 'Required' | translate }}
                          </span>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </p-panel>

        <!-- Institution Information Panel -->
        <p-panel [toggleable]="true">
          <ng-template
            let-collapsed
            pTemplate="headericons"
          >
            <i [class]="collapsed ? 'fa-solid fa-caret-right' : 'fa-solid fa-caret-down'"></i>
          </ng-template>
          <ng-template pTemplate="header">
            <div class="custom-panel-header">
              <div class="institution-nav">
                <span>{{ 'transcriptData.institution' | translate }}</span>
              </div>
            </div>
          </ng-template>
          <div class="panel-content">
            @if (transcriptData.Institutions[0]; as institution) {
              <div class="field-rows">
                @for (row of institutionFieldRows(); track $index) {
                  <div class="field-row">
                    @for (field of row; track field.id) {
                      <div class="field-item">
                        <label [for]="field.id">
                          {{ 'transcriptData.' + field.fieldKey | translate }}
                          @if (showRequiredElement(field.fieldKey, 1, '')) {
                            <span class="error-label">*</span>
                          }
                        </label>
                        <div class="input-wrapper">
                          @if (
                            shouldShowFieldError(field.fieldKey, institution[field.modelKey].value)
                          ) {
                            <i class="fa-solid fa-circle-exclamation error-icon"></i>
                          } @else if (
                            fieldsNeedingReview(field.fieldKey, institution[field.modelKey])
                          ) {
                            <i class="fa-regular fa-triangle-exclamation warning-icon"></i>
                          }
                          @if (institution[field.modelKey]?.type === 'dropdown') {
                            <p-select
                              [options]="institution[field.modelKey]?.options"
                              [id]="field.id"
                              [disabled]="!isEditable()"
                              [required]="showRequiredElement(field.fieldKey, 1, '')"
                              [appendTo]="'body'"
                              [showClear]="true"
                              [attr.aria-required]="showRequiredElement(field.fieldKey, 1, '')"
                              [attr.aria-invalid]="
                                shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                              "
                              [attr.aria-describedby]="
                                shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                                  ? field.id + '-error'
                                  : null
                              "
                              [class.has-warning]="
                                fieldsNeedingReview(field.fieldKey, institution[field.modelKey]) &&
                                !shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                              "
                              [class.has-error]="
                                shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                              "
                              [(ngModel)]="institution[field.modelKey].value"
                              (ngModelChange)="onFieldChange()"
                              (click)="showBoundingBox(institution[field.modelKey])"
                              (onClear)="onClear(institution[field.modelKey])"
                              (focus)="
                                onInputFocus(
                                  institution[field.modelKey].value,
                                  field.fieldKey,
                                  institution[field.modelKey]
                                )
                              "
                            />
                          } @else {
                            <input
                              pInputText
                              [id]="field.id"
                              [readonly]="!isEditable()"
                              [required]="showRequiredElement(field.fieldKey, 1, '')"
                              [attr.aria-required]="showRequiredElement(field.fieldKey, 1, '')"
                              [attr.aria-invalid]="
                                shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                              "
                              [attr.aria-describedby]="
                                shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                                  ? field.id + '-error'
                                  : null
                              "
                              [class.has-warning]="
                                fieldsNeedingReview(field.fieldKey, institution[field.modelKey]) &&
                                !shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                              "
                              [class.has-error]="
                                shouldShowFieldError(
                                  field.fieldKey,
                                  institution[field.modelKey].value
                                )
                              "
                              [(ngModel)]="institution[field.modelKey].value"
                              (ngModelChange)="onFieldChange()"
                              (click)="showBoundingBox(institution[field.modelKey])"
                              (focus)="
                                onInputFocus(
                                  institution[field.modelKey].value,
                                  field.fieldKey,
                                  institution[field.modelKey]
                                )
                              "
                            />
                          }
                          @if (
                            shouldShowFieldError(field.fieldKey, institution[field.modelKey].value)
                          ) {
                            <span
                              class="sr-only"
                              role="alert"
                              [id]="field.id + '-error'"
                            >
                              {{
                                'transcriptData.errors.' + field.fieldKey + 'Required' | translate
                              }}
                            </span>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </p-panel>
      </div>

      @if (showCourses()) {
        <p-panel
          class="courses-table-panel"
          [toggleable]="true"
        >
          <ng-template
            let-collapsed
            pTemplate="headericons"
          >
            <i [class]="collapsed ? 'fa-solid fa-caret-right' : 'fa-solid fa-caret-down'"></i>
          </ng-template>
          <ng-template pTemplate="header">
            <div
              #panelHeader
              class="custom-panel-header"
            >
              <div class="custom-panel-header-left">
                <span>{{ 'transcriptData.courses' | translate }}</span>
                @if (selectedCourses.length > 0) {
                  <span class="selected-count">
                    (
                    {{
                      'transcriptData.selectedRowsCount'
                        | translate: { count: selectedCourses.length }
                    }}
                    )
                  </span>
                }
              </div>
              <div class="custom-panel-header-right">
                @if (isEditable() && selectedCourses.length > 0) {
                  <p-button
                    class="dap-add-row-above"
                    icon="fa-regular fa-diagram-previous"
                    size="small"
                    severity="secondary"
                    text="true"
                    tooltipPosition="top"
                    [pTooltip]="'addRowAbove' | translate"
                    [ariaLabel]="'addRowAbove' | translate"
                    (click)="addRow('above')"
                  ></p-button>
                  <p-button
                    class="dap-add-row-below"
                    icon="fa-regular fa-diagram-next"
                    size="small"
                    severity="secondary"
                    text="true"
                    tooltipPosition="top"
                    [ariaLabel]="'addRowBelow' | translate"
                    [pTooltip]="'addRowBelow' | translate"
                    (click)="addRow('below')"
                  ></p-button>
                  <p-button
                    class="dap-delete-rows"
                    icon="fa-solid fa-trash"
                    size="small"
                    severity="secondary"
                    text="true"
                    tooltipPosition="top"
                    [ariaLabel]="'deleteRow' | translate"
                    [pTooltip]="'deleteRow' | translate"
                    (click)="confirmDeleteRows()"
                  ></p-button>
                  <p-divider layout="vertical" />
                }
                <p-button
                  #columnMenuButton
                  class="dap-toggle-columns"
                  icon="fa-regular fa-columns-3"
                  size="small"
                  severity="secondary"
                  aria-haspopup="dialog"
                  aria-controls="columnMenu"
                  [ariaLabel]="'transcriptData.columnToggle' | translate"
                  [pTooltip]="'transcriptData.columnToggle' | translate"
                  [disabled]="isExporting()"
                  [text]="true"
                  (click)="columnMenu.toggle($event)"
                ></p-button>
              </div>
            </div>
            <p-popover
              #columnMenu
              [dismissable]="true"
              [appendTo]="'body'"
            >
              <div class="column-toggle-menu">
                @for (col of courseColumns(); track col.field) {
                  <div class="menu-item">
                    <label [for]="'toggle-' + col.field">{{ col.label }}</label>
                    <p-toggleSwitch
                      class="dap-toggle-column"
                      [inputId]="'toggle-' + col.field"
                      [ngModel]="col.visible"
                      (ngModelChange)="toggleColumn(col.field)"
                    ></p-toggleSwitch>
                  </div>
                }
              </div>
            </p-popover>
          </ng-template>
          <div class="panel-content">
            <p-table
              #courseTable
              ariaLabel="{{ 'transcriptData.courseDataTable' | translate }}"
              columnResizeMode="expand"
              [resizableColumns]="true"
              [value]="transcriptData?.Courses || []"
              [dataKey]="'id'"
              [(selection)]="selectedCourses"
              (click)="$event.stopPropagation()"
            >
              <ng-template pTemplate="header">
                <tr>
                  @if (isEditable()) {
                    <th
                      scope="col"
                      class="col-select"
                    >
                      <span class="sr-only">{{ 'ITP.selectAll' | translate }}</span>
                      <p-tableHeaderCheckbox
                        [ariaLabel]="'ITP.selectAll' | translate"
                      ></p-tableHeaderCheckbox>
                    </th>
                  }
                  @for (col of visibleCourseColumns(); track col.field) {
                    <th
                      pResizableColumn
                      scope="col"
                      [pSortableColumn]="col.sortable ? col.field + '.value' : undefined"
                      [class]="'col-' + col.field.toLowerCase()"
                    >
                      <div class="flex gap-1">
                        <span
                          >{{ col.label | translate }}
                          @if (showRequiredElement(col.field, 1, '')) {
                            <span class="error-label">*</span>
                          }
                        </span>
                        @if (col.sortable) {
                          <span class="custom-sort-icon">
                            @if (getSortOrder(col.field) === 1) {
                              <i
                                class="sort-icon fa-duotone fa-solid fa-sort-up"
                                aria-hidden="true"
                              ></i>
                            }
                            @if (getSortOrder(col.field) === -1) {
                              <i
                                class="sort-icon fa-duotone fa-solid fa-sort-down"
                                aria-hidden="true"
                              ></i>
                            }
                          </span>
                        }
                      </div>
                    </th>
                  }
                </tr>
              </ng-template>
              <ng-template
                let-course
                pTemplate="body"
              >
                <tr
                  [class.selected-row]="selectedCourse === course"
                  (click)="
                    selectedCourse = course;
                    showCourseRowBoundingBox(course);
                    $event.stopPropagation()
                  "
                >
                  @if (isEditable()) {
                    <td
                      class="col-select"
                      (click)="$event.stopPropagation()"
                    >
                      <p-tableCheckbox
                        [value]="course"
                        [ariaLabel]="
                          ('ITP.select' | translate) +
                          ' ' +
                          (course['CourseTitle']?.value || course['Term']?.value || '')
                        "
                      ></p-tableCheckbox>
                    </td>
                  }
                  @for (col of visibleCourseColumns(); track col.field) {
                    <td>
                      <div class="input-container">
                        @if (course[col.field]) {
                          @if (shouldShowFieldError(col.field, course[col.field].value)) {
                            <i class="fa-solid fa-circle-exclamation error-icon-table"></i>
                          } @else if (fieldsNeedingReview(col.field, course[col.field])) {
                            <i class="fa-regular fa-triangle-exclamation warning-icon-table"></i>
                          }
                          <input
                            type="text"
                            class="table-input p-inputtext"
                            [id]="'course-' + col.field + '-' + course.id"
                            [readonly]="!isEditable()"
                            [required]="showRequiredElement(col.field, 1, '')"
                            [attr.aria-required]="showRequiredElement(col.field, 1, '')"
                            [attr.aria-invalid]="
                              shouldShowFieldError(col.field, course[col.field].value)
                            "
                            [attr.aria-describedby]="
                              shouldShowFieldError(col.field, course[col.field].value)
                                ? 'course-' + col.field + '-' + course.id + '-error'
                                : null
                            "
                            [class.has-warning]="
                              fieldsNeedingReview(col.field, course[col.field]) &&
                              !shouldShowFieldError(col.field, course[col.field].value)
                            "
                            [class.has-error]="
                              shouldShowFieldError(col.field, course[col.field].value)
                            "
                            [attr.aria-label]="col.field + ': ' + course[col.field].value"
                            [(ngModel)]="course[col.field].value"
                            (ngModelChange)="onFieldChange()"
                            (focus)="
                              onInputFocus(course[col.field].value, col.field, course[col.field])
                            "
                            (click)="showCourseRowBoundingBox(course)"
                          />
                          @if (shouldShowFieldError(col.field, course[col.field].value)) {
                            <span
                              class="sr-only"
                              role="alert"
                              [id]="'course-' + col.field + '-' + course.id + '-error'"
                              >{{
                                'transcriptData.errors.fieldRequired'
                                  | translate: { field: (col.label | translate) }
                              }}</span
                            >
                          }
                        }
                      </div>
                    </td>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="visibleCourseColumns().length || 1">
                    <div
                      class="empty-message-container dap-course-empty-message"
                      role="status"
                    >
                      <span>{{ 'transcriptData.noCourses' | translate }}</span>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </p-panel>
      }
      <!-- Courses Panel -->
    }
  </div>

  <!-- Footer -->
  <div
    class="transcript-data-footer"
    role="contentinfo"
  >
    <div class="footer-left">
      @if (selectedField()) {
        <div class="selected-cell-info">
          <span class="info-label">{{ 'transcriptData.value' | translate }}:</span>
          <span class="info-value">{{
            selectedInputValue() || ('transcriptData.emptyValue' | translate)
          }}</span>
        </div>
      }
      @if (selectedField() && isEditable() && reviewIndicatorFlag()) {
        <p-divider layout="vertical" />

        <div class="selected-cell-reviewed">
          <p-checkbox
            class="dap-reviewed-checkbox"
            inputId="fieldReviewed"
            [binary]="true"
            [ngModel]="selectedField()?.reviewed ?? false"
            (ngModelChange)="onReviewedChange($event)"
          ></p-checkbox>
          <label for="fieldReviewed">{{ 'reviewedLabel' | translate }}</label>
        </div>
      }
    </div>

    <div class="footer-right">
      @if (
        isEditable() &&
        (transcriptStatusId() === TranscriptStatus.ReadyForExport ||
          transcriptStatusId() === TranscriptStatus.NeedsReview ||
          transcriptStatusId() === TranscriptStatus.FailedToExport ||
          transcriptStatusId() === TranscriptStatus.FailedMatching ||
          transcriptStatusId() === TranscriptStatus.PartiallyExported) &&
        enableSendToSis()
      ) {
        <div class="review-toggle-container flex items-center gap-2">
          <p-toggleSwitch
            class="dap-review-toggle"
            inputId="reviewToggle"
            [ngModel]="isReviewCompleted()"
            [disabled]="!isEditable()"
            (ngModelChange)="onReviewCompletedChange($event)"
          ></p-toggleSwitch>
          <label
            for="reviewToggle"
            class="review-toggle-label"
            >{{ 'transcriptData.reviewCompleted' | translate }}</label
          >
        </div>
        <p-divider layout="vertical" />
      }
      <div class="footer-right-buttons">
        <p-button
          class="dap-close-transcript"
          type="button"
          variant="text"
          severity="secondary"
          [label]="'close' | translate"
          [attr.aria-label]="'close' | translate"
          [disabled]="isSaving() || hasUnsavedChanges() || isExporting()"
          (click)="onClose()"
        ></p-button>

        @if (
          (isEditable() || isExporting()) &&
          (transcriptStatusId() === TranscriptStatus.ReadyForExport ||
            transcriptStatusId() === TranscriptStatus.NeedsReview ||
            transcriptStatusId() === TranscriptStatus.FailedToExport ||
            transcriptStatusId() === TranscriptStatus.FailedMatching ||
            transcriptStatusId() === TranscriptStatus.PartiallyExported) &&
          enableSendToSis()
        ) {
          <p-button
            class="dap-export-btn"
            severity="secondary"
            [label]="(isExporting() ? 'ITP.exporting' : 'ITP.sendToSis') | translate"
            [attr.aria-label]="(isExporting() ? 'ITP.exporting' : 'ITP.sendToSis') | translate"
            [disabled]="!isReviewCompleted() || isSaving() || hasUnsavedChanges() || isExporting()"
            [loading]="isExporting()"
            (click)="onClickSendToSis()"
          ></p-button>
        } @else if (
          isEditable() && (transcriptStatusId() === TranscriptStatus.Exported || !enableSendToSis())
        ) {
          <p-button
            class="dap-archive-transcript"
            severity="secondary"
            [label]="'ITP.archive' | translate"
            [attr.aria-label]="'ITP.archive' | translate"
            [disabled]="isSaving() || hasUnsavedChanges()"
            (click)="onComplete()"
          ></p-button>
        }
      </div>
    </div>
  </div>
}

<p-dialog
  [header]="'transcriptData.archiveTranscriptProcess' | translate"
  [modal]="true"
  [closable]="false"
  [resizable]="false"
  [(visible)]="showCompletionConfirmation"
  (onHide)="showCompletionConfirmation.set(false)"
>
  <p class="dialog-content">{{ 'transcriptData.archivingTranscript' | translate }}</p>
  <p class="confirm-message">{{ 'transcriptData.confirmTranscriptArchival' | translate }}</p>
  <div class="dialog-footer">
    <p-button
      class="dap-cancel-archive"
      variant="text"
      [label]="'cancel' | translate"
      (onClick)="handleCompletion(false)"
    >
    </p-button>
    <p-button
      class="dap-confirm-archive"
      severity="secondary"
      [label]="'transcriptData.archive' | translate"
      (onClick)="handleCompletion(true)"
    >
    </p-button>
  </div>
</p-dialog>

<p-dialog
  class="p-dialog-source"
  [header]="'transcriptData.sendToSIS' | translate"
  [modal]="true"
  [closable]="false"
  [resizable]="false"
  [(visible)]="showSendToSISConfirmation"
  (onHide)="showSendToSISConfirmation.set(false)"
>
  <p class="dialog-content">{{ 'ITP.sendToSisConfirmation' | translate }}</p>
  <div class="dialog-footer">
    <p-button
      class="dap-cancel-send-to-sis"
      variant="text"
      [label]="'cancel' | translate"
      (onClick)="handleCancelSendToSis()"
    >
    </p-button>
    <p-button
      class="dap-confirm-send-to-sis"
      severity="secondary"
      [label]="'ITP.sendToSis' | translate"
      (onClick)="sendToSis(documentId, router.url)"
    >
    </p-button>
  </div>
</p-dialog>

<p-dialog
  class="p-dialog-source"
  [header]="'ITP.changeType' | translate"
  [modal]="true"
  [closable]="false"
  [resizable]="false"
  [(visible)]="showChangeTypeConfirmation"
  (onHide)="handleChangeTypeConfirmation(false)"
>
  <p class="dialog-content">{{ 'ITP.changeTypeConfirmation' | translate }}</p>
  <div class="dialog-footer">
    <p-button
      class="dap-cancel-change-type"
      variant="text"
      [label]="'cancel' | translate"
      (onClick)="handleChangeTypeConfirmation(false)"
    >
    </p-button>
    <p-button
      class="dap-confirm-change-type"
      severity="secondary"
      [label]="'ITP.changeType' | translate"
      (onClick)="handleChangeTypeConfirmation(true)"
    >
    </p-button>
  </div>
</p-dialog>

<p-dialog
  class="missing-fields-dialog"
  [header]="'transcriptData.missingRequiredFields' | translate"
  [modal]="true"
  [closable]="true"
  [resizable]="false"
  [(visible)]="showMissingFieldsDialog"
>
  <div class="missing-fields-content">
    <p>{{ 'transcriptData.missingFieldsMessage' | translate }}</p>
    <ul class="missing-sections-list">
      @for (section of missingFieldsSections(); track section) {
        <li>{{ getSectionKey(section) | translate }}</li>
      }
    </ul>
  </div>
  <ng-template pTemplate="footer">
    <p-button
      class="dap-close-missing-fields"
      [label]="'close' | translate"
      (onClick)="closeMissingFieldsDialog()"
    ></p-button>
  </ng-template>
</p-dialog>

<p-dialog
  [header]="'transcriptData.deleteCourseRows' | translate"
  [modal]="true"
  [dismissableMask]="true"
  [closable]="true"
  [resizable]="false"
  [draggable]="false"
  [(visible)]="showDeleteCourseRowsConfirmation"
  (onHide)="showDeleteCourseRowsConfirmation.set(false)"
>
  <p
    class="dialog-content"
    [innerHTML]="
      'transcriptData.deleteCourseRowsMessage' | translate: { count: selectedCourses.length }
    "
  ></p>
  <p
    class="confirm-message"
    [innerHTML]="
      'transcriptData.confirmDeleteCourseRows' | translate: { count: selectedCourses.length }
    "
  ></p>
  <div class="dialog-footer">
    <p-button
      class="dap-cancel-delete-rows"
      variant="text"
      [label]="'cancel' | translate"
      (onClick)="handleDeleteRowsConfirmation(false)"
    ></p-button>
    <p-button
      class="dap-confirm-delete-rows"
      severity="danger"
      variant="outlined"
      [label]="'delete' | translate"
      (onClick)="handleDeleteRowsConfirmation(true)"
    ></p-button>
  </div>
</p-dialog>

<app-itp-extraction-confidence
  [field]="selectedConfidenceField"
  [customExplanation]="selectedConfidenceExplanation"
  [fieldsNeedingAttention]="fieldsNeedingAttentionCount()"
  [visible]="confidenceModalVisible"
  (visibleChange)="onConfidenceModalClose()"
>
</app-itp-extraction-confidence>
